import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Star, Upload, Download, Check, Pencil, X, GitMerge } from "lucide-react";
import { DEFAULT_CATEGORIES } from "../../lib/constants";

const FILTERS = [
  { key: "", label: "All unverified" },
  { key: "missing_email", label: "Missing email" },
  { key: "missing_instagram", label: "Missing Instagram" },
  { key: "missing_website", label: "Missing website" },
  { key: "partner", label: "Starred partner" },
];

const SyncBadge = ({ sync }) => {
  if (!sync || !Object.keys(sync).length)
    return <span className="text-[#5c5852] text-xs">—</span>;
  return (
    <span className="text-xs space-x-1">
      {["dwh", "dwt"].map((p) => {
        const st = sync[p]?.status || "";
        const cls = st === "synced" ? "text-[#5c6e53]" : st === "skipped_no_email" || st === "not_configured" ? "text-[#5c5852]" : "text-[#b35c5c]";
        return <span key={p} className={cls} title={sync[p]?.error || st}>{p.toUpperCase()}:{st === "synced" ? "✓" : st === "skipped_no_email" ? "no email" : st === "not_configured" ? "n/c" : "err"}</span>;
      })}
    </span>
  );
};

const EditRow = ({ v, categories, onSave, onCancel }) => {
  const [f, setF] = useState({ name: v.name, type: v.type || "", email: v.email || "", instagram: v.instagram || "", website: v.website || "", contact_name: v.contact_name || "" });
  return (
    <tr className="bg-[#f6f2ec]">
      <td colSpan={8} className="px-4 py-3">
        <div className="flex flex-wrap gap-2 items-center">
          <input className="px-2 py-1 border border-[#dcd3c6] text-sm w-48" value={f.name} data-testid={`edit-name-${v.id}`} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Name" />
          <select className="px-2 py-1 border border-[#dcd3c6] text-sm" value={f.type} data-testid={`edit-type-${v.id}`} onChange={(e) => setF({ ...f, type: e.target.value })}>
            <option value="">— category —</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <input className="px-2 py-1 border border-[#dcd3c6] text-sm w-52" value={f.email} data-testid={`edit-email-${v.id}`} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="Email" />
          <input className="px-2 py-1 border border-[#dcd3c6] text-sm w-36" value={f.instagram} data-testid={`edit-instagram-${v.id}`} onChange={(e) => setF({ ...f, instagram: e.target.value })} placeholder="Instagram" />
          <input className="px-2 py-1 border border-[#dcd3c6] text-sm w-56" value={f.website} data-testid={`edit-website-${v.id}`} onChange={(e) => setF({ ...f, website: e.target.value })} placeholder="Website" />
          <input className="px-2 py-1 border border-[#dcd3c6] text-sm w-36" value={f.contact_name} onChange={(e) => setF({ ...f, contact_name: e.target.value })} placeholder="Contact" />
          <button className="btw-btn text-xs px-3 py-1.5" data-testid={`edit-save-${v.id}`} onClick={() => onSave(v.id, f)}>Save</button>
          <button className="text-xs underline" onClick={onCancel}>Cancel</button>
        </div>
      </td>
    </tr>
  );
};

export default function ReviewVendors() {
  const [data, setData] = useState({ vendors: [], total: 0, verified: 0, counts: {} });
  const [filter, setFilter] = useState("");
  const [category, setCategory] = useState("");
  const [q, setQ] = useState("");
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [selected, setSelected] = useState({});
  const [editing, setEditing] = useState(null);
  const [dupes, setDupes] = useState(null);
  const [dupeKeep, setDupeKeep] = useState({});
  const [showDupes, setShowDupes] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);

  const load = async () => {
    const p = new URLSearchParams();
    if (filter) p.set("filter", filter);
    if (category) p.set("category", category);
    if (q) p.set("q", q);
    const r = await api.get(`/admin/vendors/review${p.toString() ? "?" + p.toString() : ""}`);
    setData(r.data);
  };
  const loadDupes = async () => {
    const r = await api.get("/admin/vendors/duplicates");
    setDupes(r.data.groups);
  };

  useEffect(() => {
    api.get("/meta/categories").then((r) => setCategories(r.data.categories || DEFAULT_CATEGORIES));
  }, []);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [filter, category, q]);

  const pct = data.total ? Math.round((data.verified / data.total) * 100) : 0;

  const saveEdit = async (id, f) => {
    await api.patch(`/admin/vendors/${id}`, f);
    setEditing(null);
    toast.success("Saved");
    load();
  };
  const verifyOne = async (id) => {
    const r = await api.post(`/admin/vendors/${id}/verify`);
    const errs = ["dwh", "dwt"].filter((p) => r.data.ghl_featured_sync?.[p]?.status === "error");
    if (errs.length) toast.error(`Verified, but GHL sync failed (${errs.join("/")}) — retry from the vendor page`);
    else toast.success("Verified");
    load();
  };
  const togglePin = async (v) => {
    await api.patch(`/admin/vendors/${v.id}`, { star_pinned: !v.star_pinned, is_partner: v.star_pinned ? v.is_partner : true });
    toast.success(v.star_pinned ? "Star unpinned" : "Star pinned — sync will not remove it");
    load();
  };
  const bulkVerify = async () => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (!ids.length) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/vendors/bulk-verify", { ids });
      toast.success(`Verified ${r.data.verified}${r.data.failed?.length ? `, ${r.data.failed.length} with GHL errors` : ""}`);
      setSelected({});
      load();
    } finally {
      setBusy(false);
    }
  };
  const exportCsv = () => {
    const p = new URLSearchParams();
    if (filter) p.set("filter", filter);
    if (category) p.set("category", category);
    if (q) p.set("q", q);
    api.get(`/admin/vendors/export?${p.toString()}`, { responseType: "blob" }).then((r) => {
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "vendors.csv";
      a.click();
      URL.revokeObjectURL(url);
    });
  };
  const mergeDupe = async (group) => {
    const keep = dupeKeep[group.key];
    if (!keep) return toast.error("Pick the record to keep");
    const drop = group.vendors.find((x) => x.id !== keep);
    if (!drop) return;
    const r = await api.post("/admin/vendors/merge", { keep_id: keep, drop_id: drop.id });
    toast.success(`Merged — ${r.data.credits_moved} credit(s) moved`);
    loadDupes();
    load();
  };

  const doPreview = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/vendors/import/preview", fd);
      setPreview(r.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not read that CSV. Check the file format and try again.");
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };
  const doCommit = async () => {
    setImporting(true);
    try {
      const r = await api.post("/admin/vendors/import/commit", { staging_id: preview.staging_id });
      toast.success(`Imported ${r.data.imported} vendors`);
      setPreview(null);
      load();
    } finally {
      setImporting(false);
    }
  };

  const allChecked = data.vendors.length > 0 && data.vendors.every((v) => selected[v.id]);

  return (
    <div className="p-6 lg:p-10" data-testid="review-vendors-page">
      <div className="mb-6">
        <p className="btw-overline mb-1">Vendor directory</p>
        <h1 className="text-3xl font-bold">Review imported vendors</h1>
      </div>

      <div className="bg-white border border-[#dcd3c6] p-4 mb-6" data-testid="import-card">
        <p className="text-xs uppercase tracking-wider text-[#5c5852] mb-2">One-off master CSV import</p>
        <p className="text-xs text-[#5c5852] mb-3">Choose a CSV with Name and Type columns. Email found and Contact columns are accepted too. Review the preview, then select Confirm import to save the vendors.</p>
        {!preview ? (
          <div className="flex items-center gap-3 flex-wrap">
            <input ref={fileRef} type="file" accept=".csv" data-testid="import-file" className="text-sm" onChange={(e) => doPreview(e.target.files[0])} />
            {importing && <span className="text-xs text-[#5c5852]">Parsing…</span>}
          </div>
        ) : (
          <div data-testid="import-preview">
            <p className="text-sm mb-2">
              Ready to import <b>{preview.to_import}</b> vendors · <b>{preview.with_email ?? 0}</b> with email · <b>{preview.with_contact ?? 0}</b> with contact name · <b>{preview.merged}</b> duplicates merged · <b>{preview.skipped}</b> rows skipped
              {preview.possible_matches_count > 0 && <> · <b className="text-[#b35c5c]">{preview.possible_matches_count} possible match(es) with existing vendors</b> (not merged — review below)</>}
            </p>
            {preview.parse_errors?.length > 0 && (
              <div className="text-xs text-[#b35c5c] mb-2">
                {preview.parse_error_count > preview.parse_errors.length && <p>Showing {preview.parse_errors.length} of {preview.parse_error_count} row warnings.</p>}
                <ul>{preview.parse_errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
              </div>
            )}
            {preview.possible_matches?.length > 0 && (
              <ul className="text-xs mb-2 max-h-28 overflow-y-auto" data-testid="import-matches">
                {preview.possible_matches.map((m, i) => <li key={i}>⚠ {m.import_name} → existing “{m.existing_name}”</li>)}
              </ul>
            )}
            <div className="flex gap-2">
              <button className="btw-btn text-xs px-4 py-2" data-testid="import-confirm" disabled={importing} onClick={doCommit}>{importing ? "Importing…" : "Confirm import"}</button>
              <button className="text-xs underline" data-testid="import-cancel" onClick={() => setPreview(null)}>Cancel</button>
            </div>
          </div>
        )}
      </div>

      <div className="mb-4">
        <div className="flex justify-between text-xs text-[#5c5852] mb-1">
          <span>Verification progress</span>
          <span data-testid="review-progress-text">{data.verified} / {data.total} verified</span>
        </div>
        <div className="h-2 bg-[#e2d3c0] w-full">
          <div className="h-2 bg-[#768c6a] transition-all" style={{ width: `${pct}%` }} data-testid="review-progress-bar" />
        </div>
      </div>

      <div className="flex gap-3 items-center flex-wrap mb-4">
        {FILTERS.map((f) => (
          <button key={f.key} data-testid={`filter-${f.key || "all"}`} onClick={() => setFilter(f.key)}
            className={`text-xs px-3 py-1.5 border ${filter === f.key ? "bg-[#3d4a36] text-white border-[#3d4a36]" : "border-[#dcd3c6] bg-white"}`}>
            {f.label}{f.key ? ` (${data.counts?.[f.key] ?? 0})` : ""}
          </button>
        ))}
        <select data-testid="review-category-filter" value={category} onChange={(e) => setCategory(e.target.value)} className="px-3 py-1.5 border border-[#dcd3c6] bg-white text-xs">
          <option value="">All categories</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <input placeholder="Search…" data-testid="review-search" className="px-3 py-1.5 border border-[#dcd3c6] bg-white text-xs" value={q} onChange={(e) => setQ(e.target.value)} />
        <button className="btw-btn text-xs px-3 py-1.5 inline-flex items-center gap-1" data-testid="bulk-verify" disabled={busy} onClick={bulkVerify}><Check size={13} /> Verify selected</button>
        <button className="btw-btn-outline text-xs px-3 py-1.5 inline-flex items-center gap-1" data-testid="export-csv" onClick={exportCsv}><Download size={13} /> Export CSV</button>
        <button className="text-xs underline inline-flex items-center gap-1" data-testid="toggle-dupes" onClick={() => { setShowDupes(!showDupes); if (dupes === null) loadDupes(); }}>
          <GitMerge size={13} /> Suggested duplicates {dupes ? `(${dupes.length})` : ""}
        </button>
      </div>

      {showDupes && dupes && (
        <div className="bg-white border border-[#dcd3c6] mb-4 p-4" data-testid="dupes-panel">
          <p className="text-xs uppercase tracking-wider text-[#5c5852] mb-2">Suggested duplicates — pick the record to keep, then merge</p>
          {dupes.length === 0 && <p className="text-sm text-[#5c5852]">No duplicates found.</p>}
          {dupes.map((g) => (
            <div key={g.key} className="border-t border-[#e2d3c0] py-2 flex items-center gap-4 flex-wrap" data-testid={`dupe-${g.key}`}>
              <span className="text-xs text-[#5c5852] w-36">{g.reason}</span>
              {g.vendors.map((v) => (
                <label key={v.id} className="text-sm inline-flex items-center gap-1">
                  <input type="radio" name={g.key} data-testid={`dupe-keep-${v.id}`} checked={dupeKeep[g.key] === v.id} onChange={() => setDupeKeep({ ...dupeKeep, [g.key]: v.id })} />
                  {v.name} <span className="text-xs text-[#5c5852]">({v.type || "no type"}{v.is_approved ? ", verified" : ""})</span>
                </label>
              ))}
              <button className="btw-btn text-xs px-3 py-1" data-testid={`dupe-merge-${g.key}`} onClick={() => mergeDupe(g)}>Merge</button>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white border border-[#dcd3c6] overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-wider text-[#5c5852] border-b border-[#dcd3c6]">
              <th className="px-4 py-3 w-8"><input type="checkbox" data-testid="select-all" checked={allChecked} onChange={(e) => setSelected(Object.fromEntries(data.vendors.map((v) => [v.id, e.target.checked])))} /></th>
              <th className="text-left px-2 py-3 w-8" title="Star pinned (sync won't remove)">★</th>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Category</th>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Instagram</th>
              <th className="text-left px-4 py-3">Website</th>
              <th className="text-left px-4 py-3">GHL</th>
              <th className="text-right px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {data.vendors.map((v) => (
              editing === v.id ? (
                <EditRow key={v.id} v={v} categories={categories} onSave={saveEdit} onCancel={() => setEditing(null)} />
              ) : (
                <tr key={v.id} className="border-t border-[#e2d3c0]" data-testid={`review-row-${v.id}`}>
                  <td className="px-4 py-2"><input type="checkbox" data-testid={`select-${v.id}`} checked={!!selected[v.id]} onChange={(e) => setSelected({ ...selected, [v.id]: e.target.checked })} /></td>
                  <td className="px-2 py-2">
                    <button onClick={() => togglePin(v)} data-testid={`pin-${v.id}`} title={v.star_pinned ? "Star pinned — sync won't remove" : v.is_partner ? "Partner (click to pin)" : "Not a partner"}>
                      <Star size={15} className={v.is_partner ? (v.star_pinned ? "fill-[#c19872] text-[#876a4f]" : "fill-[#e2d3c0] text-[#876a4f]") : "text-[#dcd3c6]"} />
                    </button>
                  </td>
                  <td className="px-4 py-2">{v.name}</td>
                  <td className="px-4 py-2 text-xs">{v.type}</td>
                  <td className="px-4 py-2 text-xs">{v.email || <span className="text-[#b35c5c]">missing</span>}</td>
                  <td className="px-4 py-2 text-xs">{v.instagram || <span className="text-[#b35c5c]">missing</span>}</td>
                  <td className="px-4 py-2 text-xs max-w-[180px] truncate">{v.website || <span className="text-[#b35c5c]">missing</span>}</td>
                  <td className="px-4 py-2"><SyncBadge sync={v.ghl_featured_sync} /></td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    <button className="text-xs underline mr-2" data-testid={`edit-${v.id}`} onClick={() => setEditing(v.id)}><Pencil size={12} className="inline" /> Edit</button>
                    <button className="btw-btn text-xs px-2 py-1" data-testid={`verify-${v.id}`} onClick={() => verifyOne(v.id)}>Verify</button>
                  </td>
                </tr>
              )
            ))}
            {data.vendors.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-8 text-center text-sm text-[#5c5852]">Nothing to review here.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
